import { EhrConnector, getMe } from "@elation/api-client/src/stitch-web";
import { useSyncPatientByEhr } from "@elation/api-client/src/stitch-web/stitchWeb";
import { authClient, Session } from "@src/auth/client";
import { BEARER_TOKEN_KEY } from "@src/auth/client.constants";
import { IS_CHROME_EXTENSION, IS_IN_IFRAME } from "@src/env";
import {
  useAuthTokenFromParent,
  useEhrFromIframe,
  usePatientFromIframe,
} from "@src/features/iframe";
import { navigation } from "@src/navigation";
import { chromeExtensionOktaLogin } from "@src/okta/chrome";
import { config } from "@src/okta/config";
import { LoginPage, type Values } from "@src/pages/Login";
import { LoginIframePage } from "@src/pages/LoginIframe";
import { appStore } from "@src/stores/app";
import {
  iframeLoginMethod,
  trackUserLoggedIn,
  type LoginMethod,
} from "@src/utils/observability/trackUserLoggedIn";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useNavigate,
  useSearchParams,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from "react-router-dom";

export function meta() {
  return [{ title: "Login" }, { name: "description", content: "Login" }];
}

export function clientLoader(_: LoaderFunctionArgs) {
  return {
    initialEmail: process.env.LOCAL_EMAIL || "",
    initialPassword: process.env.LOCAL_PASSWORD || "",
  };
}

export async function clientAction(_args: ActionFunctionArgs) {
  return {};
}

function IframeLoginFlow(props: {
  isLoading: boolean;
  error?: string | null;
  session: Session | null;
}) {
  const { isLoading, error, session } = props;

  const patientFromIframe = usePatientFromIframe();
  const ehrFromIframe = useEhrFromIframe();

  const syncPatient = useSyncPatientByEhr();
  const {
    mutate: syncMutate,
    isPending: isSyncLoading,
    error: syncError,
    data: syncData,
  } = syncPatient;

  // React 18 StrictMode fires effects twice in dev, and the mutation's `isIdle`
  // transitions asynchronously after `syncMutate` — so an `isIdle` guard races
  // with the state transition and can fire the request twice per mount. Use a
  // synchronous ref flag to guarantee exactly one request per (ehr, patientId).
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    appStore.getState().setPracticeTimezone(undefined);
  }, []);

  useEffect(
    function triggerSyncOnce() {
      if (
        !IS_IN_IFRAME ||
        !ehrFromIframe ||
        !patientFromIframe?.id ||
        !session
      ) {
        return;
      }

      const fireKey = `${ehrFromIframe}:${patientFromIframe.id}`;

      if (firedForRef.current === fireKey) {
        return;
      }

      firedForRef.current = fireKey;

      syncMutate({
        ehr: ehrFromIframe as EhrConnector,
        patientId: patientFromIframe.id.toString(),
      });

      const method = iframeLoginMethod(ehrFromIframe as EhrConnector);

      getMe()
        .then(({ data: me }) => trackUserLoggedIn(me, method))
        .catch((err) => {
          console.error("Failed to record user_logged_in RUM event:", err);
        });
    },
    [ehrFromIframe, patientFromIframe?.id, syncMutate, session],
  );

  const iframeConfigurationError = useMemo(() => {
    if (!ehrFromIframe) {
      return "No EHR connector found";
    }

    return null;
  }, [ehrFromIframe]);

  // The bearer in localStorage is what kept the iframe in the dead-end
  // state — clear it so the next mount re-asks the parent for a token via
  // `requestAuthTokenFromParent` rather than re-attempting auth with the
  // same stale value.
  const handleRetry = () => {
    localStorage.removeItem(BEARER_TOKEN_KEY);
    window.location.reload();
  };

  if (!patientFromIframe) {
    return <LoginIframePage isLoading />;
  }

  if (syncError) {
    return <Navigate to={navigation.patients()} replace />;
  }

  if (syncData) {
    return (
      <Navigate
        to={navigation.patientBriefing(syncData.data.patient.id.toString())}
        replace
      />
    );
  }

  const displayError = error || iframeConfigurationError;

  return (
    <LoginIframePage
      isLoading={isLoading || isSyncLoading}
      error={displayError}
      onRetry={displayError ? handleRetry : undefined}
    />
  );
}

export default function LoginForClient() {
  const {
    data: session,
    isPending: isPendingSession,
    error: sessionError,
    refetch,
  } = authClient.useSession();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errorFromUrl = searchParams.get("error");

  const sessionErrorMessage = sessionError?.message
    ? "Unable to verify your session. Please try logging in again."
    : undefined;

  const loginError = errorFromUrl || error || sessionErrorMessage;

  const trackLoginSuccess = async (method: LoginMethod) => {
    try {
      const { data: me } = await getMe();
      trackUserLoggedIn(me, method);
    } catch (err) {
      console.error("Failed to record user_logged_in RUM event:", err);
    }
  };

  const handleLoginWithStitch = async (values: Values) => {
    try {
      const result = await authClient.signIn.email(values, {
        onSuccess: async () => {
          await trackLoginSuccess("email");
          navigate(navigation.dashboard(), { replace: true });
        },
      });

      if (result.error) {
        setError(result.error.message ?? "An unknown error occurred");
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "An unexpected error occurred",
      );
    }
  };

  const handleOktaLogin = async (values: Values) => {
    setIsLoading(true);
    setError(null);

    try {
      if (IS_CHROME_EXTENSION) {
        const result = await chromeExtensionOktaLogin(
          {
            clientId: config.clientId,
            issuer: config.issuer,
            scopes: config.scopes,
          },
          values.type,
        );

        if (result.success) {
          await trackLoginSuccess("okta_chrome");

          navigate(navigation.chromeExtensionWaitingRoom(), {
            replace: true,
          });
        } else {
          setError(result.error || "Login failed");
        }
      } else {
        const oauthConfig = {
          providerId: "okta",
          callbackURL: window.location.origin + "/login/callback",
          newUserCallbackURL: window.location.origin + "/dashboard",
          scopes: config.scopes,
        };

        await authClient.signIn.oauth2(oauthConfig);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred",
      );
      setIsLoading(false);
    }
  };

  useAuthTokenFromParent(refetch);

  if (IS_IN_IFRAME) {
    return (
      <IframeLoginFlow
        isLoading={isLoading || isPendingSession}
        error={loginError}
        session={session}
      />
    );
  }

  if (session && !loginError) {
    return <Navigate to={navigation.dashboard()} replace />;
  }

  return (
    <LoginPage
      onLogin={handleLoginWithStitch}
      onLoginWithElation={handleOktaLogin}
      isLoading={isLoading || isPendingSession}
      error={loginError}
    />
  );
}
